import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function TelfordAndWrekinCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Telford and Wrekin Pothole Claims | 102km of B/C Roads Amber | Fixtyer"
        description="Telford and Wrekin Council's own data shows 31% of B and C roads flagged amber for maintenance — 102.3km on a 965km network where 54% of roads are surveyed only every four years. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/telford-and-wrekin"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Telford and Wrekin', url: '/councils/telford-and-wrekin' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Telford and Wrekin has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because the council projects £15.108m capital spend against a £13.108m DfT allocation in 2025/26. But Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate budgets. The overall and condition ratings are AMBER, and the council\'s own data shows B and C roads in green condition fell from 75% to 64% while amber roads rose to 31%.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 525km — 54.4% of Telford and Wrekin\'s 965km network. The council surveys only 25% of U-roads each year, delivering a full network survey over a four-year cycle. At the last five comparable surveys, 5–6% of U-roads were in RED condition — roughly 26–31km of estate and residential routes. The survey gap is a structural argument under Section 58 if your road was not in the current year\'s 25% sample.',
          },
          {
            question: 'Does the 2021 switch from SCANNER to AEI surveys affect my claim?',
            answer: 'It matters for comparability, not for liability. Telford and Wrekin moved to Annual Engineer Inspection surveys in 2021 and acknowledges AEI "may indicate a higher proportion of the network in different condition compared to SCANNER." A-road RED condition rose from 2% pre-2021 to 7% under AEI — a methodology shift, not necessarily a sudden physical collapse. For your claim, focus on whether the council knew about the specific defect through safety inspections, reports or its own survey data on your road section.',
          },
          {
            question: 'Capital spend nearly halved in 2024/25 — does that weaken the council\'s defence?',
            answer: 'It can help your case contextually. Total capital spend fell from £16.516m in 2020/21 to £7.893m in 2024/25, and reactive maintenance rose to 34% of spend that year — the highest in five years. The council projects a rebound to £15.108m in 2025/26, but a year of reduced preventative investment coinciding with higher reactive share suggests defects were forming faster than structural renewal could keep pace.',
          },
          {
            question: 'The council sometimes uses temporary pothole repairs — does that help my claim?',
            answer: 'It can. Telford and Wrekin\'s own pothole guidance states that most repairs are permanent, but if maintenance is planned later in the year or the road is due to be inspected, the repair may be temporary. A temporary patch on a road already flagged amber or red at the last survey suggests the underlying surface failure was known — and that reactive patching did not restore the carriageway to a maintainable standard.',
          },
          {
            question: 'Why is the DfT Best Practice scorecard AMBER?',
            answer: 'The DfT rates documented processes and inspection coverage, not just budgets. Telford and Wrekin has asset management plans and risk-based inspections, but surveys only a quarter of U-roads annually, changed condition methodology in 2021, and aligns to emerging PAS 2161 standards from 2026/27. The AMBER best practice rating signals room for improvement in published evidence and network-wide survey coverage — relevant when arguing whether the council had reasonable knowledge of your road\'s condition.',
          },
          {
            question: 'Do I need to report the pothole before claiming?',
            answer: 'Reporting creates a dated council record — useful if the pothole was reported before your incident, or if the council failed to repair it within a reasonable time. Telford and Wrekin inspects all reported defects and aims to repair dangerous ones within one working day. Keep your reference number and any confirmation emails regardless of pothole size.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Telford and Wrekin Council"
        councilSlug="telford-and-wrekin"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/telford-and-wrekin"
        headline="Telford and Wrekin Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Telford and Wrekin</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Telford and Wrekin: 102km of B and C roads need maintenance soon
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Telford and Wrekin Council&apos;s own Annual Engineer Inspection data flags{' '}
            <strong className="text-amber-400">31% of B and C roads amber</strong> — 102.3km where
            &ldquo;maintenance may be required soon&rdquo; — while green-condition classified roads fell from
            75% to 64% in five years. The DfT rates overall maintenance{' '}
            <strong className="text-amber-400">AMBER</strong>, spend{' '}
            <strong className="text-emerald-400">GREEN</strong>, and best practice{' '}
            <strong className="text-amber-400">AMBER</strong>. Meanwhile{' '}
            <strong className="text-rose-400">525km of unclassified roads</strong> — more than half the
            borough — are condition-surveyed on a four-year rotation.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">31%</div>
            <div className="text-white font-medium mt-2">B and C roads flagged amber (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              102.3km of the borough&apos;s 330km classified local road network — up from 21% (69.3km) in
              2020. Green B/C roads fell 11 percentage points over the same period.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard cards */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the DfT AMBER rating means — and what it doesn&apos;t
          </h2>
          <p className="text-surface-600 mb-8">
            Three scorecards from the Department for Transport&apos;s 2025/26 local road maintenance ratings,
            cross-checked against Telford and Wrekin&apos;s own June 2025 transparency report
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <RatingBadge rating="amber" size="md" />
                <span className="text-sm font-semibold text-amber-900">Condition</span>
              </div>
              <div className="text-3xl font-bold text-amber-600 mb-2">31%</div>
              <div className="text-sm text-amber-900 font-medium">B/C roads in amber (2024)</div>
              <div className="text-xs text-surface-500 mt-2">
                A-roads hold at 7% RED under AEI surveys. But classified local roads are slipping — green
                B/C roads down 11 points since 2020 while amber roads climbed 10 points.
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <RatingBadge rating="green" size="md" />
                <span className="text-sm font-semibold text-emerald-900">Spend</span>
              </div>
              <div className="text-3xl font-bold text-emerald-600 mb-2">£15.1m</div>
              <div className="text-sm text-emerald-900 font-medium">Projected capital spend 2025/26</div>
              <div className="text-xs text-surface-500 mt-2">
                Against a £13.108m DfT allocation, with 78% targeted at preventative maintenance. Spend
                rebounded after capital investment fell to £7.893m in 2024/25.
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <RatingBadge rating="amber" size="md" />
                <span className="text-sm font-semibold text-amber-900">Best practice</span>
              </div>
              <div className="text-3xl font-bold text-amber-600 mb-2">4 years</div>
              <div className="text-sm text-amber-900 font-medium">Full U-road survey cycle</div>
              <div className="text-xs text-surface-500 mt-2">
                Only 25% of unclassified roads surveyed each year. AEI methodology replaced SCANNER in 2021,
                aligning to emerging PAS 2161 standards from 2026/27.
              </div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The honest read</h3>
            <p className="text-brand-200 mb-4">
              Telford and Wrekin is not a failing council. It projects spending above its DfT allocation,
              targets roughly three-quarters of investment at preventative works, and runs risk-based safety
              inspections across 965km of roads, 352km of footways and 36,000 drainage gullies. A generic
              template claim on a recently resurfaced A-road will face a structured Section 58 defence.
            </p>
            <p className="text-white font-medium">
              But the DfT rates programmes, not potholes. The council&apos;s own data shows B/C amber roads
              at a five-year high, reactive maintenance peaking at 34% of spend in 2024/25, and more than
              half the network on a four-year condition survey cycle. The rating doesn&apos;t decide your
              claim — the evidence about <em>your</em> defect does.
            </p>
          </div>
        </div>
      </section>

      {/* The condition picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of AEI survey data from Telford and Wrekin&apos;s own transparency report — classified
            roads surveyed annually, unclassified roads on a four-year rotation
          </p>

          {/* Network context */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">965km</div>
              <div className="text-sm text-surface-600">Total road network</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-amber-700">525km</div>
              <div className="text-sm text-amber-800">Unclassified roads (54.4%)</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">330km</div>
              <div className="text-sm text-surface-600">B and C roads (34.2%)</div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (110km — 11.4% of network): stable RED under AEI</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">7%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 2% pre-2021 SCANNER</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">32%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 13% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">61%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 85% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              All A-roads are surveyed annually. The council switched from SCANNER to Annual Engineer
              Inspection in 2021 and notes AEI may show higher proportions in amber and red categories
              than the previous methodology — the 2% to 7% RED shift largely reflects a different ruler,
              not necessarily a sudden collapse of main routes.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (330km — 34.2% of network): amber rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">80%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">81%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">5%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">31%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">64%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Amber B/C roads jumped from 21% to 31% in four years — an extra 33km flagged for maintenance.
              Green roads fell from 75% to 64%. A third of the B/C network now needs — or will soon need —
              intervention, even before counting the 5% in RED (16.5km).
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The 2024/25 investment dip</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£13.1m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£15.1m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">£7.9m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Capital spend nearly halved in 2024/25 — down from £16.5m in 2020/21 — while reactive
              maintenance climbed to <span className="text-rose-400">34%</span> of total spend, the
              highest share in five years. The 2025/26 rebound is projected, not yet delivered.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road survey cycle */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 525km four-year survey cycle
          </h2>
          <p className="text-surface-600 mb-8">
            54.4% of the network is unclassified roads — condition-surveyed on a rolling four-year programme
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 25%-per-year gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Telford and Wrekin&apos;s own report states that 25% of U-roads are surveyed annually,
                delivering a full network survey over a four-year period in accordance with DfT requirements.
                That means any given residential street may go up to four years between condition surveys.
              </p>
              <p className="text-surface-600 text-sm">
                At 5–6% RED, roughly <strong>26–31km of unclassified roads</strong> were flagged for
                maintenance at each survey — estate streets, village routes and residential cul-de-sacs
                across the borough.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">DfT minimum, not best practice</h3>
              <p className="text-amber-800 text-sm mb-3">
                A four-year U-road cycle meets DfT reporting requirements, but classified A and B/C roads
                are surveyed every year. The council knows the condition of its main routes annually — yet
                the majority of the network, where most residents live, is measured on a slower timetable.
              </p>
              <p className="text-amber-800 text-sm">
                For Section 58, ask when your specific road was last condition-surveyed — and whether
                deterioration between surveys was tracked through safety inspections or resident reports.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Telford and Wrekin&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last included in the 25% annual U-road survey sample?</li>
              <li>• If 5–6% of U-roads were RED at the last borough-wide survey, what was done about yours?</li>
              <li>• How does the council track deterioration on a road it may not condition-survey for four years?</li>
              <li>• Did routine safety inspections on your route pick up the defect before it damaged your vehicle?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge where condition data is four years old —
              and safety inspections are separate from structural condition surveys.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            14,282 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from the council&apos;s published transparency tables — minor works only,
            excluding patching and resurfacing schemes
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes repaired (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">3,167</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,884</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,534</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,155</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,542</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14,282</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~2,850 repairs a year on average</h3>
              <p className="text-surface-600 text-sm">
                The council&apos;s own report states an average of approximately 2,850 pothole repairs per
                year. These figures exclude repairs undertaken as part of patching or resurfacing schemes
                — so the true defect count on the network is higher. The 2023/24 spike to 3,155 coincided
                with the lowest capital spend year.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 projection: 2,500 fills</h3>
              <p className="text-amber-800 text-sm">
                The council estimates 2,500 pothole fills in 2025/26 alongside 5.2km of carriageway
                resurfacing, 12.6km of preservation treatments and 6,000m² of patching. On a 965km network,
                even a full delivery of planned works touches a fraction of the carriageway each year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AEI methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2021 survey methodology change
          </h2>
          <p className="text-surface-600 mb-8">
            Why pre- and post-2021 condition figures need careful reading — in the council&apos;s own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="While AEI may indicate a higher proportion of the network in different condition compared to SCANNER, it reflects the holistic, engineering-led evaluation of entire lengths of road. This provides a more accurate representation of maintenance needs and aligns with delivery on the ground."
              source="Telford and Wrekin Council — Annual Local Highway Maintenance Transparency Report (June 2025)"
            />
          </div>

          <p className="text-surface-600 text-sm">
            A-road RED condition rose from 2% under SCANNER to a stable 7% under AEI from 2021. The council
            aligns to emerging PAS 2161 standards from 2026/27 — part of why the DfT Best Practice scorecard
            is AMBER. For claims, use post-2021 AEI trends on your road class rather than comparing 2020
            SCANNER baselines directly to current figures.
          </p>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER council with GREEN spend
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Telford and Wrekin invests above its DfT allocation — here&apos;s how that
            changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projects £15.108m capital against £13.108m DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> ~75% of five-year spend classed as preventative on average
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy and risk-based safety inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Dangerous reported defects targeted for repair within one working day
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on recently surveyed classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 31% of B/C roads flagged amber, green down 11 points since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 525km of U-roads on a four-year condition survey cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive maintenance peaked at 34% in 2024/25 as capital spend hit a five-year low
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 14,282 pothole repairs in five years — defects form between inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits temporary repairs and documented climate risks including Ironbridge Gorge
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN spend scorecard, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (MyTelford, council online form) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the four-year survey cycle is your strongest structural argument</li>
              <li>• Whether the repair at your location was documented as temporary</li>
              <li>• Location relative to known flooding or ground stability risk areas</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Telford and Wrekin&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Telford and Wrekin first
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting through the council creates a dated record — useful evidence if the pothole was reported
            before your incident, or if the council failed to repair it within a reasonable time. Telford and
            Wrekin inspects all reported defects and aims to repair dangerous ones within one working day.
            Keep your reference number and any confirmation emails.
          </p>
          <a
            href="https://www.telford.gov.uk/roadworks-transport-and-streets/streets-and-roads/street-care-and-cleaning/potholes/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Telford and Wrekin Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            For emergencies outside office hours (Monday to Friday, 9am to 5pm), call 03451 55 99 55.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Telford and Wrekin?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded AMBER council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No 2024/25 reactive-spend context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C amber roads at 31% documented</li>
                <li>✅ Four-year U-road survey gap argued</li>
                <li>✅ 14,282 pothole repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Telford and Wrekin</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=telford-and-wrekin"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Telford and Wrekin claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Telford and Wrekin has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because the council projects £15.108m capital spend
                against a £13.108m DfT allocation in 2025/26. But Section 58 turns on whether the specific
                defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate
                budgets. The overall and condition ratings are AMBER, and the council&apos;s own data shows
                B and C roads in green condition fell from 75% to 64% while amber roads rose to 31%.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 525km — 54.4% of Telford and Wrekin&apos;s 965km network. The
                council surveys only 25% of U-roads each year, delivering a full network survey over a
                four-year cycle. At the last five comparable surveys, 5–6% of U-roads were in RED condition.
                The survey gap is a structural argument under Section 58 if your road was not in the current
                year&apos;s 25% sample.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 2021 switch from SCANNER to AEI surveys affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It matters for comparability, not for liability. Telford and Wrekin moved to Annual Engineer
                Inspection surveys in 2021 and acknowledges AEI may show higher amber and red proportions
                than SCANNER. For your claim, focus on whether the council knew about the specific defect
                through safety inspections, reports or its own survey data on your road section.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Capital spend nearly halved in 2024/25 — does that weaken the council&apos;s defence?
              </h3>
              <p className="text-surface-600 text-sm">
                It can help your case contextually. Total capital spend fell from £16.516m in 2020/21 to
                £7.893m in 2024/25, and reactive maintenance rose to 34% of spend that year. The council
                projects a rebound to £15.108m in 2025/26, but a year of reduced preventative investment
                coinciding with higher reactive share suggests defects were forming faster than structural
                renewal could keep pace.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council sometimes uses temporary pothole repairs — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Telford and Wrekin&apos;s own pothole guidance states that most repairs are permanent,
                but if maintenance is planned later in the year or the road is due to be inspected, the repair
                may be temporary. A temporary patch on a road already flagged amber or red at the last survey
                suggests the underlying surface failure was known.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I need to report the pothole before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Reporting creates a dated council record — useful if the pothole was reported before your
                incident, or if the council failed to repair it within a reasonable time. Telford and Wrekin
                inspects all reported defects and aims to repair dangerous ones within one working day. Keep
                your reference number and any confirmation emails regardless of pothole size.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.telford.gov.uk/roadworks-transport-and-streets/roadworks-projects-and-traffic-management/planned-projects-and-maintenance/planned-highways-and-transport-projects/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Telford and Wrekin Council — Annual Local Highway Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
